import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useGlobalLoading, useToast } from '../../../../context';
import type { RootState } from '../../../../store';
import type { ChatResourceItem } from '../components/resources/ResourceCard';
import {
  addConsumerContent,
  WEBINY_MODEL_END_ZONE_PATIENT_RESOURCE,
} from '../../../../api';

interface UseChatResourceActionsOptions {
  caremapprAppUserId?: string | null;
}

const normalizeResourceUrl = (url?: string) => {
  const trimmedUrl = typeof url === 'string' ? url.trim() : '';
  if (!trimmedUrl) {
    return null;
  }

  const nextUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmedUrl)
    ? trimmedUrl
    : trimmedUrl.startsWith('//')
      ? `https:${trimmedUrl}`
      : `https://${trimmedUrl.replace(/^\/+/, '')}`;

  try {
    return new URL(nextUrl).toString();
  } catch {
    return null;
  }
};

const useChatResourceActions = (options?: UseChatResourceActionsOptions) => {
  const { startLoading, stopLoading } = useGlobalLoading();
  const { setToast } = useToast();
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const resolvedCaremapprAppUserId = options?.caremapprAppUserId ?? null;
  const tenancyId = selectedTenancy?.tenancy_id ?? null;

  const openResourceUrl = useCallback((resource: ChatResourceItem) => {
    const normalizedUrl = normalizeResourceUrl(resource.url);
    if (!normalizedUrl) {
      return;
    }

    window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const addResourceToPatientPortal = useCallback(
    async (resource: ChatResourceItem) => {
      if (!resource.entryId) {
        return;
      }

      if (!resolvedCaremapprAppUserId || tenancyId == null) {
        setToast('Failed to add resource to patient portal app ❌');
        return;
      }

      startLoading('Adding resource to patient portal app…');

      try {
        await addConsumerContent({
          entryId: resource.entryId,
          tenancyId,
          caremapprAppUserId: resolvedCaremapprAppUserId,
          webinyModel: WEBINY_MODEL_END_ZONE_PATIENT_RESOURCE,
        });

        setToast('Resource sent to consumer’s patient portal ✅');
      } catch (e) {
        console.log(e);
        setToast('Failed to add resource to patient portal app ❌');
      } finally {
        stopLoading();
      }
    },
    [
      resolvedCaremapprAppUserId,
      tenancyId,
      setToast,
      startLoading,
      stopLoading,
    ],
  );

  return {
    openResourceUrl,
    addResourceToPatientPortal,
  };
};

export default useChatResourceActions;
